
import * as three from 'three';
import { isFiniteNum,isValidNum } from '../number';
import { Vec } from './vec';
import { Vec2Like } from './vec-like';
import { Vec3 } from './vec3';

export class Vec2 extends three.Vector2 implements Vec{
	public override toString(){
		return `(${this.x},${this.y})`;
	}

	public toXY(){
		return new Vec2(this.x,this.y);
	}

	public toXYZ(z:number){
		return new Vec3(this.x,this.y,z);
	}

	public toVec(){
		return new Vec2(this.x,this.y);
	}

	public applyFunc(fn:(v:number, axis:'x'|'y')=>number):this{
		this.x=fn(this.x,'x');
		this.y=fn(this.y,'y');
		return this;
	}

	public setAll(v:number){
		this.x=v;
		this.y=v;
		return this;
	}

	public override set(axis:|'x'|'y', v:number):this;
	public override set(x:number, y:number):this;
	public override set(arg0:number|'x'|'y', arg1:number){
		if(typeof(arg0)==='string'){
			this[arg0]=arg1;
		}else{
			super.set(arg0,arg1);
		}
		return this;
	}

	public neg(){
		this.x*=-1;
		this.y*=-1;
		return this;
	}

	public inv(){
		this.x=1/this.x;
		this.y=1/this.y;
		return this;
	}

	public abs(){
		return this.applyFunc(Math.abs);
	}

	public addScaled(that:Vec2Like, scale:number){
		this.x+=that.x*scale;
		this.y+=that.y*scale;
		return this;
	}

	public mul(that:Vec2Like){
		this.x*=that.x;
		this.y*=that.y;
		return this;
	}

	public div(that:Vec2Like){
		this.x/=that.x;
		this.y/=that.y;
		return this;
	}

	public mod(that:Vec2Like){
		this.x%=that.x;
		this.y%=that.y;
		return this;
	}

	public override lerp(that:Vec2Like, f:number){
		this.x+=(that.x-this.x)*f;
		this.y+=(that.y-this.y)*f;
		return this;
	}

	public rerp(that:Vec2Like, f:number){
		const  angle=this.angleTo(that);
		this.rotate(angle*f);
		return this;
	}

	public mean(that:Vec2Like){
		this.x=(that.x+this.x)/2;
		this.y=(that.y+this.y)/2;
		return this;
	}

	public mix(that:Vec2Like, f:Vec2Like){
		this.x+=(that.x-this.x)*f.x;
		this.y+=(that.y-this.y)*f.y;
		return this;
	}

	public sum(a:Vec2Like, b:Vec2Like){
		this.x=a.x+b.x;
		this.y=a.y+b.y;
		return this;
	}

	public diff(a:Vec2Like, b:Vec2Like){
		this.x=a.x-b.x;
		this.y=a.y-b.y;
		return this;
	}

	public delta(that:Vec2Like){
		return this.toXY().sub(that);
	}

	public blend(a:Vec2Like, b:Vec2Like, f:number){
		this.x=a.x+(b.x-a.x)*f;
		this.y=a.y+(b.y-a.y)*f;
		return this;
	}

	public translate(x:number, y:number){
		this.x+=x;
		this.y+=y;
		return this;
	}

	public scale(x:number, y=x){
		this.x*=x;
		this.y*=y;
		return this;
	}

	public invScale(x:number, y=x){
		this.x/=x;
		this.y/=y;
		return this;
	}

	public frac(){
		this.x%=1;
		this.y%=1
		return this;
	}

	public lenSq(){
		return this.x**2+this.y**2;
	}

	public len(){
		return Math.sqrt(this.x**2+this.y**2);
	}

	public angleTo(that:Vec2Like){
		const la=this.len();
		const lb=Math.sqrt(that.x**2+that.y**2);

		const xa=this.x/la;
		const ya=this.y/la;
		const xb=that.x/lb;
		const yb=that.y/lb;
		let angle=Math.acos((xa*xb)+(ya*yb));
		if((xa*-yb)+(ya*xb)>0)
			angle*=-1;
		return angle;
	}

	public nrm(){
		let l=this.len();
		if(l>0)
			this.scale(1/l);
		return this;
	}

	public distToSq(that:Vec2Like){
		return (this.x-that.x)**2+(this.y-that.y)**2;
	}

	public distTo(that:Vec2Like){
		return Math.sqrt(this.distToSq(that));
	}

	public distToSqXY(that:Vec2Like){
		return this.distToSq(that);
	}

	public distToXY(that:Vec2Like){
		return this.distTo(that);
	}

	public eq(that:Vec2Like){
		return that && this.x===that.x && this.y===that.y;
	}

	public isValid(){
		return isValidNum(this.x) && isValidNum(this.y);
	}

	public isFinite(){
		return isFiniteNum(this.x) && isFiniteNum(this.y);
	}

	public rotate(angle:number){
		const c=Math.cos(angle);
		const s=Math.sin(angle);
		const x=this.x;
		const y=this.y;
		this.x=x*c-y*s;
		this.y=x*s+y*c;
		return this;
	}

	public rotate90(){
		//[v.x,v.y]=[-v.y,v.x];
		const tmp=this.x;
		this.x=-this.y;
		this.y=tmp;
		return this;
	}

	public rotateXY(angle:number){
		return this.rotate(angle);
	}

	public rotate90XY(){
		return this.rotate90();
	}

	public azimuth():number|undefined;
	public azimuth(azimuth:number, len?:number):this
	public azimuth(azimuth?:number, len?:number):number|undefined|this{
		return Vec.azimuth(this,azimuth,len);
	}

	public applyMatrix4(m:three.Matrix4){
		const {x,y}=this;
		const e=m.elements;

		const w=1/(e[3]*x+e[7]*y+e[15]);
		this.x=(e[0]*x+e[4]*y+e[12])*w;
		this.y=(e[1]*x+e[5]*y+e[13])*w;
		return this;
	}
}

export interface Vec2{
	readonly vecType:Vec2;
	readonly componentsType:'x'|'y';
	readonly cloneType:this;

	add(that:Vec2Like):this;
	clamp(min:Vec2Like, max:Vec2Like):this;
	copy(that:Vec2Like):this;
	dot(that:Vec2Like):number;
	lerp(that:Vec2Like, f:number):this;
	max(that:Vec2Like):this;
	min(that:Vec2Like):this;
	sub(that:Vec2Like):this;
	toArray<T extends ArrayLike<number>>(array:T, offset?:number):T;
	toArray():[number,number];

	readonly tmpA:Vec2;
	readonly tmpB:Vec2;
	readonly tmpC:Vec2;
}

export namespace Vec2{
	export const tmpA=new Vec2();
	export const tmpB=new Vec2();
	export const tmpC=new Vec2();

	export function isLike(v:any):v is Vec2Like{
		return typeof(v)==='object' && 'x' in v && 'y' in v;
	}

	export function fromArray(array:ArrayLike<number>, offset?:number){
		return new Vec2().fromArray(array,offset);
	}

	export function from(src:ArrayLike<number>|Vec2Like):Vec2;
	export function from(src:null):null;
	export function from(src:ArrayLike<number>|Vec2Like|null):Vec2|null;
	export function from(src:ArrayLike<number>|Vec2Like|null){
		if(!src)
			return null;
		if('x' in src)
			return new Vec2(src.x,src.y);
		return fromArray(src);
	}

	export function fromAzimuth(azimuth:number, len?:number):Vec2{
		return new Vec2(1,0).azimuth(azimuth,len);
	}
}

export function vec2(x:number, y:number){
	return new Vec2(x,y);
}

(<any>Vec2.prototype).tmpA=Vec2.tmpA;
(<any>Vec2.prototype).tmpB=Vec2.tmpB;
(<any>Vec2.prototype).tmpC=Vec2.tmpC;
